<script setup>
import _ from 'lodash';
import { watch, ref, computed, onMounted, onUnmounted, watchEffect } from 'vue';
import { useRouter, onBeforeRouteLeave } from 'vue-router';
import { useTransformsStore } from '../../stores/transforms.store';
import AppLoader from '../../components/AppLoader.vue';
import DynamicField from '../../components/DynamicField.vue';
import PageBreadcrumb from '../../components/PageBreadcrumb.vue';
import AppFooter from '../../components/AppFooter.vue';
import ErrorMessage from '../../components/ErrorMessage.vue';
import { ChevronDown } from 'lucide-vue-next';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { useBreadcrumbs } from '@/composables/useBreadcrumbs';
import { toast } from '@/composables/useToast';
import ActionMenu from '@/components/ActionMenu.vue';
import { adjustControlConditionalValues, adjustControlConditionalVisibility, filterUserConfigurableConfigs } from '@/utils/utils';
import { getTransformIcon } from '@/utils/constants';

const props = defineProps({
    transformType: {
        type: String,
        default: null
    },
    cloneConfig: {
        type: Object,
        default: null
    }
});

const transformsStore = useTransformsStore();
const transformCreatePayload = ref({});
let formError = ref('');
let loading = ref(false);
let loadingList = ref(false);
let advancedMode = ref(false);
const hidePromptTemporary = ref(false);
const router = useRouter();
const { setDynamicLabel, clearDynamicLabel } = useBreadcrumbs();

const hideLateralDescriptionValue = ref(true);

onBeforeRouteLeave((to, from, next) => {
    if (loadingList.value) {
        return next();
    }

    if (hidePromptTemporary.value) {
        return next();
    }

    const answer = window.confirm('Are you sure you want to leave? You may have unsaved changes.');

    if (answer) {
        return next();
    } else {
        return next(false);
    }
});

const transformTypeTitle = computed(() => {
    if (transformsStore.configs) {
        const found = Object.values(transformsStore.configs).find(
            (x) => x.filter((y) => (y.transform || y.transform_type) === props.transformType).length
        );

        return found?.[0]?.display_name;
    }
});

const transformTypeControls = computed(() => {
    if (transformsStore.configs) {
        const found = Object.values(transformsStore.configs).find(
            (x) => x.filter((y) => (y.transform || y.transform_type) === props.transformType).length
        );

        return found?.[0]?.config;
    }
});

// Update breadcrumb when transform type title is available
watchEffect(() => {
    if (transformTypeTitle.value) {
        setDynamicLabel('create-transform-type', `Add Transform - ${transformTypeTitle.value}`);
    }
});

const handleResize = () => {
    hideLateralDescriptionValue.value = window.innerWidth <= 1024;
};

onMounted(() => {
    handleResize();
});

window.addEventListener('resize', handleResize);

onUnmounted(() => {
    window.removeEventListener('resize', handleResize);
    clearDynamicLabel('create-transform-type');
});

onMounted(async () => {
    await getTransformConfigs();

    watch(props, async ({ transformType }) => {
        if (transformType && !transformTypeTitle.value) await getTransformConfigs();
    });
});

const onSaveTransform = async () => {
    loading.value = true;

    const config = Object.values(transformCreatePayload.value).reduce((payload, { name, value }) => ({ ...payload, [name]: value }), {});
    hidePromptTemporary.value = true;
    try {
        const data = await transformsStore.create({
            transform: props.transformType,
            config
        });

        toast.success('Transform created successfully.');
        if (data?.id) {
            // Navigate with clean state - no query parameters that might interfere
            router.push({
                name: 'transform-details',
                params: { id: data.id },
                query: {} // Explicitly clean query to prevent filter state conflicts
            });
        } else {
            router.push({ name: 'transforms' });
        }
    } catch (err) {
        formError.value = err;
        hidePromptTemporary.value = false;
    } finally {
        loading.value = false;
    }
};

const onFieldChanged = ({ value, valid, name }) => {
    const control = transformTypeControls.value.find((x) => x.name == name);
    const index = transformTypeControls.value.findIndex((x) => x.name == name);

    if (control) {
        control.value.current_value = value;
        control.value.valid = valid;

        transformTypeControls.value[index] = control;
    }

    transformCreatePayload.value[name] = {
        value,
        valid,
        name
    };
};

const transformTypeConfig = computed(() => {
    const configurations = transformTypeControls.value;

    if (!configurations) return null;

    let transformConfig = _.cloneDeep(configurations);

    if (props.cloneConfig && transformConfig) {
        const cloneConfig = JSON.parse(props.cloneConfig);

        transformConfig = _.map(transformConfig, (configItem) => {
            if (configItem.encrypt || configItem.not_clonable) {
                if (configItem.not_clonable) {
                    configItem.value.default = undefined;
                }

                return configItem;
            }

            const clonedConfigValue = cloneConfig[configItem.name];
            if (_.isNil(clonedConfigValue)) return configItem;

            return {
                ...configItem,
                value: {
                    ...configItem.value,
                    default: clonedConfigValue
                }
            };
        });
    }

    return transformConfig;
});

const visibleBasicTransformConfigControls = computed(() => {
    const filteredConfigs = filterUserConfigurableConfigs(transformTypeConfig.value);
    return _.chain(adjustControlConditionalValues(filteredConfigs))
        .filter((x) => !x.display_advanced)
        .orderBy('order_of_display', 'asc')
        .map((control) => adjustControlConditionalVisibility(control, filteredConfigs)?.control || null)
        .compact()
        .value();
});

const visibleAdvancedTransformConfigControls = computed(() => {
    const filteredConfigs = filterUserConfigurableConfigs(transformTypeConfig.value);
    return _.chain(adjustControlConditionalValues(filteredConfigs))
        .filter((x) => x.display_advanced)
        .orderBy('order_of_display', 'asc')
        .map((control) => adjustControlConditionalVisibility(control, filteredConfigs)?.control || null)
        .compact()
        .value();
});

const enableSave = computed(() => {
    if (props.transformType && transformTypeConfig) {
        const filteredConfigs = filterUserConfigurableConfigs(transformTypeConfig.value);
        return filteredConfigs.reduce((valid, { name, display_in_ui }) => {
            return display_in_ui ? valid && transformCreatePayload.value[name] && transformCreatePayload.value[name].valid : true;
        }, true);
    } else {
        return false;
    }
});

async function getTransformConfigs() {
    try {
        loadingList.value = true;

        await transformsStore.fetchTransformConfigs();
    } catch (error) {
        console.error('Error fetching transforms config', error);
    } finally {
        loadingList.value = false;
    }
}

const getClass = (control, index, advanced = false) => {
    const spaceLeft = control.space_left || 0; // Default depth is 0.
    const usedTransformConfigs = advanced ? visibleAdvancedTransformConfigControls.value : visibleBasicTransformConfigControls.value;
    const nextControlSpaceLeft = usedTransformConfigs[index + 1]?.space_left;

    const paddingLeftClass = spaceLeft > 0 ? `pl-${spaceLeft * 5}` : '';

    let marginBottomClass = 'mb-6'; // default margin for same level
    if (nextControlSpaceLeft === undefined) {
        // Last control: use standard margin.
        marginBottomClass = 'mb-6';
    } else if (nextControlSpaceLeft > spaceLeft) {
        // Next control is a child of the current control: smaller margin.
        marginBottomClass = 'mb-2';
    } else if (nextControlSpaceLeft === spaceLeft) {
        // Next control is at the same level: standard margin.
        marginBottomClass = 'mb-6';
    } else if (nextControlSpaceLeft < spaceLeft) {
        // Next control is at a higher level (ending a group): larger margin.
        marginBottomClass = 'mb-8';
    }

    return `${paddingLeftClass} ${marginBottomClass}`.trim();
};
</script>

<template>
    <!-- PageBreadcrumb teleports itself to App.vue header -->
    <PageBreadcrumb />

    <div>
        <AppLoader :force-loading="loadingList" default-height="75vh">
            <div v-if="transformTypeTitle && props.transformType" class="flex justify-between items-center mb-3">
                <div class="flex flex-row gap-3 items-center">
                    <Avatar class="h-12 w-16 bg-background dark:bg-slate-200 border border-border rounded-md shrink-0">
                        <AvatarImage :src="getTransformIcon(props.transformType)" :alt="`${props.transformType} icon`" class="object-contain p-1" />
                        <AvatarFallback
                            class="bg-background dark:bg-slate-200 dark:text-slate-800 text-muted-foreground font-semibold text-lg rounded-md">
                            {{ props.transformType?.charAt(0).toUpperCase() }}
                        </AvatarFallback>
                    </Avatar>
                    <span class="text-lg font-medium text-foreground">{{ transformTypeTitle }}</span>
                </div>
            </div>

            <div v-if="transformTypeConfig" class="flex flex-row justify-center w-full">
                <div class="w-full">
                    <Card>
                        <CardContent class="p-6">
                            <form class="grid grid-cols-1 sm:grid-cols-1 sm:gap-x-8" action="#" method="POST" autocomplete="new-password">
                                <div v-show="control.display_in_ui" v-for="(control, index) in visibleBasicTransformConfigControls" :key="index">
                                    <DynamicField
                                        :key="control.name"
                                        :config="control"
                                        :input-class="getClass(control, index)"
                                        :hide-lateral-description="hideLateralDescriptionValue"
                                        @input="onFieldChanged" />
                                </div>

                                <div v-show="visibleAdvancedTransformConfigControls?.filter((c) => c.display_in_ui).length > 0">
                                    <div
                                        class="flex w-full items-center justify-between border px-4 py-3 font-medium transition-all hover:bg-accent cursor-pointer"
                                        :class="advancedMode ? 'rounded-t-lg border-b-0' : 'rounded-lg'"
                                        @click="advancedMode = !advancedMode">
                                        Advanced
                                        <ChevronDown
                                            class="h-4 w-4 shrink-0 transition-transform duration-200"
                                            :class="advancedMode ? 'rotate-180' : ''" />
                                    </div>
                                    <div v-show="advancedMode" class="border border-t-0 rounded-b-lg p-5">
                                        <div class="grid grid-cols-1 sm:grid-cols-1 sm:gap-x-8">
                                            <div
                                                v-show="control.display_in_ui"
                                                v-for="(control, index) in visibleAdvancedTransformConfigControls"
                                                :key="index">
                                                <DynamicField
                                                    :key="control.name"
                                                    :config="control"
                                                    :input-class="getClass(control, index, true)"
                                                    :hide-lateral-description="hideLateralDescriptionValue"
                                                    @input="onFieldChanged" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </form>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </AppLoader>

        <!-- Footer will teleport to App.vue footer slot -->
        <AppFooter v-if="transformTypeConfig">
            <template #right>
                <div v-if="formError" class="flex flex-col text-md font-semibold items-end mr-10">
                    <ErrorMessage v-if="formError" :error="formError" :to-sentry="true" :context="'create-transform'" />
                </div>
                <ActionMenu
                    variant="row"
                    :show-save="true"
                    :show-discard="false"
                    :disable-save="!enableSave || loading"
                    :loading="loading"
                    @save="onSaveTransform" />
            </template>
        </AppFooter>
    </div>
</template>
